import axios from 'axios';
import { ozmapConfig } from '../ozmapConfig';

// Configuração da instância do Axios
const api = axios.create({
    baseURL: ozmapConfig.API_URL,
    headers: { 'Authorization': `Bearer ${ozmapConfig.API_TOKEN}` }
});

// Helper para implementar backoff exponencial
const fetchWithBackoff = async (fn, retries = 3, delay = 1000) => {
    try {
        return await fn();
    } catch (error) {
        if (retries > 0 && error.response && error.response.status === 429) {
            console.warn(`Rate limit atingido (429). Tentando novamente em ${delay}ms...`);
            await new Promise(resolve => setTimeout(resolve, delay));
            return fetchWithBackoff(fn, retries - 1, delay * 2);
        }
        throw error;
    }
};

/**
 * Função genérica para realizar requisições GET na API com suporte a backoff.
 * @param {string} endpoint O endpoint da API a ser chamado.
 * @param {object} config A configuração para a requisição Axios.
 * @returns {Promise<any>} Uma promessa que resolve para os dados da resposta.
 */
const get = async (endpoint, config = {}) => {
    return fetchWithBackoff(async () => {
        try {
            const response = await api.get(endpoint, config);
            return response.data;
        } catch (error) {
            console.error(`ERRO ao chamar o endpoint '${endpoint}':`, error.message);
            throw error;
        }
    });
};

/**
 * Helper para buscar todos os registros usando paginação.
 * Respeita o limite máximo de 1000 registros por requisição.
 */
const fetchAllPages = async (endpoint, config = {}) => {
    let allRows = [];
    let page = 1;
    const limit = 1000; // Limite seguro imposto pela API
    let keepFetching = true;

    // Preserva os parâmetros originais
    const originalParams = config.params || {};

    while (keepFetching) {
        const currentConfig = {
            ...config,
            params: {
                ...originalParams,
                page: page,
                limit: limit
            }
        };

        try {
            const data = await get(endpoint, currentConfig);

            // Lida com diferentes formatos de resposta (array direto ou objeto com rows)
            const rows = Array.isArray(data) ? data : (Array.isArray(data.rows) ? data.rows : []);

            if (rows.length === 0) {
                keepFetching = false;
            } else {
                allRows = allRows.concat(rows);

                // Se retornou menos que o limite, chegamos ao fim
                if (rows.length < limit) {
                    keepFetching = false;
                } else {
                    page++;
                }
            }
        } catch (error) {
            console.error(`Erro na paginação do endpoint ${endpoint}, página ${page}:`, error.message);
            throw error; // Propaga o erro para ser tratado pelo chamador
        }
    }
    return allRows;
};

/**
 * Normaliza uma string, removendo acentos e convertendo para maiúsculas.
 * @param {string} str A string a ser normalizada.
 * @returns {string} A string normalizada.
 */
const normalizeString = (str) => {
    if (!str) return '';
    return str
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toUpperCase();
};

/**
 * Converte lat/lng/raio para número. A API rejeita (422) valores em string,
 * e as coordenadas podem chegar como texto vindas do formulário/Firestore.
 */
const toNumericGeo = (coords, radius) => ({
    lat: Number(coords?.lat),
    lng: Number(coords?.lng),
    radius: Number(radius)
});

const _fetchWithLatLng = async (endpoint, projectId, coords, radius) => {
    const geo = toNumericGeo(coords, radius);
    const params = {
        project: projectId,
        lat: geo.lat,
        lng: geo.lng,
        radius: geo.radius
    };
    // Usa a paginação automática para garantir todos os registros
    return fetchAllPages(endpoint, { params });
};

/**
 * O campo `client` do imóvel vem com o objeto do cliente embutido (ou null).
 * Aceita também o formato de ID solto, por segurança.
 */
const clientIdOf = (client) => {
    if (!client) return null;
    if (typeof client === 'string') return client;
    return client.id || client._id || null;
};

/**
 * Clientes que não entram na contagem da área afetada:
 * - `implanted: false` — cadastrado mas ainda não instalado;
 * - `status: 1` — não é o status corrente de atendimento.
 *
 * A exclusão é por valor específico (e não por uma lista de permitidos) para
 * que outros status eventualmente existentes continuem sendo contados em vez
 * de sumirem silenciosamente.
 */
const isCountableClient = (client) => {
    if (!client || typeof client === 'string') return true; // sem dados para avaliar
    return client.implanted !== false && client.status !== 1;
};

export const ozmapService = {
    getProjectByName: (projectName) => {
        const normalizedProjectName = normalizeString(projectName);
        const projectId = ozmapConfig.PROJECTS[normalizedProjectName];
        if (projectId) {
            return Promise.resolve(projectId);
        }
        return Promise.reject(new Error(`Projeto "${projectName}" (normalizado para "${normalizedProjectName}") não encontrado no ozmapConfig.js.`));
    },

    _fetchWithNearFilter: async (endpoint, projectId, coords, radius, selectFields = null) => {
        const filter = JSON.stringify([{
            property: 'coords',
            value: toNumericGeo(coords, radius),
            operator: 'near'
        }]);

        const params = {
            project: projectId,
            filter: filter
        };

        if (selectFields) {
            params.select = selectFields;
        }

        // Usa a paginação automática para garantir todos os registros
        return fetchAllPages(endpoint, { params });
    },

    fetchBoxesByRadius: function(projectId, coords, radius, selectFields = 'id name coords boxType cables color') {
        // Permite customizar os campos retornados.
        // O padrão inclui 'cables' e 'color' para manter compatibilidade com modais de Troca/Notificação.
        return this._fetchWithNearFilter('boxes', projectId, coords, radius, selectFields);
    },

    fetchPolesByRadius: function(projectId, coords, radius) {
        return this._fetchWithNearFilter('poles', projectId, coords, radius);
    },

    fetchCablesByIds: async function(ids) {
        if (!ids || ids.length === 0) return [];

        // Limita a 200 IDs por requisição para evitar URL muito longa
        // O chunk de 200 é seguro e menor que o limite da API (1000)
        const chunks = [];
        for (let i = 0; i < ids.length; i += 200) {
            chunks.push(ids.slice(i, i + 200));
        }

        const results = await Promise.all(chunks.map(async (chunk) => {
            try {
                const filter = JSON.stringify([{
                    property: '_id',
                    operator: 'in',
                    value: chunk
                }]);

                const params = {
                    filter: filter,
                    populate: ['boxA', 'boxB', 'poles.id', 'color', 'cableType'],
                    select: 'id name boxA boxB poles color cableType', // Reduz campos retornados
                    limit: 1000 // Explícito para 1000 (seguro)
                };

                const data = await get('cables', { params });

                return Array.isArray(data.rows) ? data.rows : [];
            } catch (error) {
                console.error("Erro ao buscar cabos:", error);
                return [];
            }
        }));

        return results.flat();
    },

    fetchRegionsByRadius: function(projectId, coords, radius) {
        return _fetchWithLatLng('regions', projectId, coords, radius);
    },
    
    /**
     * Conta os clientes dentro do raio informado.
     *
     * A contagem é feita sobre /properties (imóveis), e não sobre /clients: o
     * documento do cliente no OzMap tem apenas
     * `tags, code, name, observation, implanted, onu, creatorData, kind,
     *  certified, status, createdAt, updatedAt, id`
     * — sem coordenadas e sem referência à caixa. Quem carrega essa ligação é o
     * imóvel, que tem `client` (objeto do cliente já embutido, ou null), `box`,
     * `lat`/`lng`, e aceita o filtro geográfico `near`.
     *
     * Um imóvel sem `client` é um ponto de atendimento vago e não é contado;
     * clientes não implantados ou com `status: 1` também ficam de fora
     * (ver `isCountableClient`).
     *
     * @returns {Promise<number|null>} a contagem, ou `null` quando não foi possível
     * calcular — a interface precisa distinguir "nenhum cliente" de "não calculado".
     */
    fetchClientsCountByRadius: async function(projectId, coords, radius) {
        try {
            // 'select' reduz o payload; `populate: ['client']` responde 500 aqui,
            // mas o objeto do cliente já vem embutido sem precisar populate.
            const properties = await this._fetchWithNearFilter(
                'properties', projectId, coords, radius, 'id client'
            );

            // Deduplica: um mesmo cliente pode aparecer em mais de um imóvel.
            const clientIds = new Set();
            properties.forEach(property => {
                const client = property && property.client;
                if (!isCountableClient(client)) return;
                const id = clientIdOf(client);
                if (id) clientIds.add(id);
            });
            return clientIds.size;
        } catch (error) {
            console.error("[OZMAP] Não foi possível contar os clientes do raio:", error.message);
            return null;
        }
    },
};
